import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { PhoneCall, MessageSquare, Star, MapPin } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1496368077930-c1e31b4e5b44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHszfHxzZWN1cml0eSUyMGNhbWVyYXxlbnwwfHx8fDE3ODg3MTM1Njl8MA&ixlib=rb-4.1.0&q=85";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden pb-1 ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

function RadarCanvas() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let w, h, raf;
    const blips = Array.from({ length: 7 }, (_, i) => ({
      a: Math.random() * Math.PI * 2,
      r: 0.25 + Math.random() * 0.7,
      p: Math.random() * Math.PI * 2,
    }));

    const resize = () => {
      w = canvas.width = canvas.offsetWidth * devicePixelRatio;
      h = canvas.height = canvas.offsetHeight * devicePixelRatio;
    };
    resize();
    window.addEventListener("resize", resize);

    let t = 0;
    const draw = () => {
      t += 0.004;
      ctx.clearRect(0, 0, w, h);
      const cx = w / 2, cy = h / 2;
      const R = Math.min(w, h) * 0.46;

      ctx.strokeStyle = "rgba(6,182,212,0.10)";
      ctx.lineWidth = 1;
      for (let i = 1; i <= 4; i++) {
        ctx.beginPath();
        ctx.arc(cx, cy, (R * i) / 4, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.beginPath();
      ctx.moveTo(cx - R, cy); ctx.lineTo(cx + R, cy);
      ctx.moveTo(cx, cy - R); ctx.lineTo(cx, cy + R);
      ctx.stroke();

      const grad = ctx.createConicGradient
        ? ctx.createConicGradient(t * Math.PI * 2, cx, cy)
        : null;
      if (grad) {
        grad.addColorStop(0, "rgba(6,182,212,0.22)");
        grad.addColorStop(0.12, "transparent");
        grad.addColorStop(1, "transparent");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, R, 0, Math.PI * 2);
        ctx.fill();
      }

      blips.forEach((b) => {
        b.p += 0.02;
        const bx = cx + Math.cos(b.a) * b.r * R;
        const by = cy + Math.sin(b.a) * b.r * R;
        const pulse = (Math.sin(b.p) + 1) / 2;
        ctx.fillStyle = `rgba(16,185,129,${0.25 + pulse * 0.55})`;
        ctx.beginPath();
        ctx.arc(bx, by, 2.4 * devicePixelRatio, 0, Math.PI * 2);
        ctx.fill();
      });

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={ref} className="absolute inset-0 w-full h-full" aria-hidden="true" />;
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex items-center overflow-hidden grid-bg noise" data-testid="hero-section">
      <div className="absolute inset-0 opacity-70">
        <RadarCanvas />
      </div>
      <div className="scanline" />
      <div className="absolute inset-0 hero-radial" />

      <motion.div style={{ y: imgY, opacity: fade }} className="absolute right-[-8%] top-[16%] w-[46vw] max-w-[620px] hidden lg:block">
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="clip-frame relative"
        >
          <img src={HERO_IMG} alt="CCTV surveillance camera" className="w-full h-[58vh] object-cover" data-testid="hero-image" />
          <div className="absolute inset-0 hero-img-overlay" />
        </motion.div>
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-24 w-full">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.35, duration: 0.8 }}
          className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-8 font-mono2 text-[11px] uppercase tracking-[0.22em] text-strong0"
          data-testid="hero-hud"
        >
          <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-500 rec-dot" /> CAM 01 — LIVE</span>
          <span className="hidden sm:inline">LAT 28.6892° N / LONG 77.3248° E</span>
          <span className="flex items-center gap-1.5 text-amber-400"><Star className="w-3.5 h-3.5 fill-amber-400" /> 5.0 · 70 GOOGLE REVIEWS</span>
        </motion.div>

        <h1 className="font-display font-extrabold uppercase tracking-tight leading-[0.95] text-4xl sm:text-6xl lg:text-7xl max-w-4xl" data-testid="hero-headline">
          <MaskedLine delay={0.45}>Every blind spot,</MaskedLine>
          <MaskedLine delay={0.6} className="text-cyan-400">eliminated.</MaskedLine>
          <MaskedLine delay={0.75} className="text-stroke">Delhi NCR watched.</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-7 max-w-xl text-base sm:text-lg text-soft leading-relaxed"
        >
          Anaya CCTV Security Solutions — professional CP Plus &amp; Hikvision installation,
          biometric access, electric locks and video doorbells. Same-day service across
          Ghaziabad &amp; Delhi NCR by <span className="text-strong font-medium">Abdulla Ansari</span>.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="tel:7011364764"
            data-testid="hero-call-button"
            className="group flex items-center gap-3 bg-cyan-500 hover:bg-cyan-400 text-on-accent font-display font-bold px-7 py-4 rounded-full transition-all duration-300 hover:shadow-[0_0_40px_rgba(6,182,212,0.45)]"
          >
            <PhoneCall className="w-5 h-5 transition-transform duration-300 group-hover:rotate-12" />
            Call 70113 64764
          </a>
          <a
            href="https://wa.me/917011364764?text=Hi%20Anaya%20CCTV%2C%20I%20need%20a%20security%20quote"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-button"
            className="group flex items-center gap-3 border border-emerald-500/50 hover:border-emerald-400 text-emerald-400 hover:bg-emerald-500/10 font-display font-bold px-7 py-4 rounded-full transition-all duration-300"
          >
            <MessageSquare className="w-5 h-5 transition-transform duration-300 group-hover:-translate-y-0.5" />
            WhatsApp Quote
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="mt-14 flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-[0.2em] text-strong0"
        >
          <MapPin className="w-3.5 h-3.5 text-cyan-400" />
          Krishna Vihar · Mandoli · Ghaziabad · Delhi NCR
        </motion.div>
      </div>
    </section>
  );
}
